import { useEffect, useMemo, useRef, useState } from 'react'
import { gatherAllCheckedAndRequired, gatherAllForNonGranular } from '../helpers/depsHelper'

export type ServiceDep = { name: string; repoName: string; branch: string }
export type InfraDep = { name: string; branch: string }

export function useFinalDeps(params: {
  businessData: any,
  checkboxStates: Record<string, boolean>,
  granularDeployment: boolean,
  skipQuota: boolean,
  selectedBusinessUnits: string[],
  applicationBranch: string,
  deploymentBranch: string,
  deployedServices?: string[],
  manualDeps?: { appName: string; repoName: string; branch: string }[]
}) {
  const {
    businessData,
    checkboxStates,
    granularDeployment,
    skipQuota,
    selectedBusinessUnits,
    applicationBranch,
    deploymentBranch,
    deployedServices
  } = params

  const [finalServiceDeps, setFinalServiceDeps] = useState<ServiceDep[]>([])
  const [finalInfraDeps, setFinalInfraDeps] = useState<InfraDep[]>([])

  const effectiveStandalone = skipQuota

  useEffect(() => {
    if (!businessData || !businessData.releases) return

    // Manual override takes precedence
    if (params.manualDeps && params.manualDeps.length > 0) {
      const services: ServiceDep[] = []
      const infra: InfraDep[] = []
      params.manualDeps.forEach(i => {
        const rel = businessData.releases.find((r: any) => r.name === i.appName)
        if (!rel) return
        if (rel?.type === 'infrastructure') {
          infra.push({ name: i.appName, branch: i.branch })
        } else {
          services.push({ name: i.appName, repoName: i.repoName, branch: i.branch })
        }
      })
      setFinalServiceDeps(services)
      setFinalInfraDeps(infra)
      return
    }

    // Check for user selections from Step 2 - convert them to finalServiceDeps/finalInfraDeps
    const step2Raw = localStorage.getItem('step2Selections')
    let step2Selections: { selectedBusinessUnits: string[]; selectedDependencies: { name: string }[] } | null = null
    try { step2Selections = step2Raw ? JSON.parse(step2Raw) : null } catch {}

    if (step2Selections?.selectedDependencies?.length) {
      // Convert step2Selections to finalServiceDeps and finalInfraDeps
      const services: ServiceDep[] = []
      const infra: InfraDep[] = []
      
      step2Selections.selectedDependencies.forEach((dep: any) => {
        const rel = businessData.releases.find((r: any) => r.name === dep.name)
        if (!rel) {
          // If not found in business data, treat as service
          services.push({ name: dep.name, repoName: dep.name, branch: applicationBranch })
          return
        }
        
        if (rel.type === 'infrastructure') {
          infra.push({ name: rel.name, branch: deploymentBranch })
        } else {
          const repoName = rel.metadata?.centralized_repo_folder_name || rel.name
          services.push({ name: rel.name, repoName, branch: applicationBranch })
        }
      })
      
      setFinalServiceDeps(services)
      setFinalInfraDeps(infra)
      return
    }

    let selectedNames = new Set<string>()
    const deployedSet = new Set(deployedServices ?? [])

    if (granularDeployment || effectiveStandalone) {
      const forcedStates = { ...checkboxStates }
      
      // Add required dependencies for selected services
      selectedBusinessUnits.forEach(exp => {
        const topLevelServices = businessData.releases.filter((r: any) => {
          const exps = r.part_of_product || [];
          return (exps.includes(exp) || exps.includes('*')) && 
                 (r.type === 'service' || !r.type) &&
                 checkboxStates[r.name];
        });
        
        topLevelServices.forEach(svc => {
          const shouldPropagate = !effectiveStandalone || !deployedSet.has(svc.name);
          if (shouldPropagate) {
            // Mark dependencies recursively with cycle detection
            // For non-prod clusters: force required dependencies
            // For prod-like clusters: pre-select (but don't force) non-running dependencies
            const visited = new Set<string>();
            const markDependencies = (release: any, parentRequired: boolean) => {
              if (!release.dependencies || visited.has(release.name)) return;
              visited.add(release.name);
              
              release.dependencies.forEach((dep: any) => {
                const isRequired = parentRequired && !!dep.required;
                const isNotRunning = !deployedSet.has(dep.name);
                
                // For non-prod clusters: force required dependencies
                // For prod-like clusters: pre-select non-running dependencies (required or not)
                const shouldMark = effectiveStandalone 
                  ? isNotRunning  // Prod-like: pre-select any non-running dependency
                  : isRequired && isNotRunning;  // Non-prod: force only required non-running dependencies
                
                if (shouldMark) {
                  forcedStates[dep.name] = true;
                }
                
                // Recursively mark dependencies of dependencies
                const depRelease = businessData.releases.find((r: any) => r.name === dep.name);
                if (depRelease && !visited.has(depRelease.name)) {
                  markDependencies(depRelease, isRequired);
                }
              });
            };
            
            markDependencies(svc, true);
          }
        });
      });
      
      if (!effectiveStandalone) {
        businessData.releases.forEach((r: any) => {
          if ((r.part_of_product || []).includes('*')) {
            forcedStates[r.name] = true
          }
        })
      }
      
      selectedNames = gatherAllCheckedAndRequired(
        businessData,
        forcedStates,
        deployedSet,
        effectiveStandalone
      )
    } else {
      selectedNames = new Set(
        gatherAllForNonGranular(selectedBusinessUnits, businessData)
      )
    }

    const serviceDeps: ServiceDep[] = []
    const infraDeps: InfraDep[] = []

    selectedNames.forEach((name) => {
      const rel = businessData.releases.find((r: any) => r.name === name)
      if (!rel) {
        serviceDeps.push({ name, repoName: name, branch: applicationBranch })
        return
      }
      if (rel.type === 'infrastructure') {
        infraDeps.push({ name: rel.name, branch: deploymentBranch })
      } else {
        const repoName = rel.metadata?.centralized_repo_folder_name || rel.name
        serviceDeps.push({ name: rel.name, repoName, branch: applicationBranch })
      }
    })

    setFinalServiceDeps(serviceDeps)
    setFinalInfraDeps(infraDeps)
  }, [
    granularDeployment,
    skipQuota,
    selectedBusinessUnits,
    businessData,
    checkboxStates,
    applicationBranch,
    deploymentBranch,
    deployedServices,
    params.manualDeps
  ])

  return { finalServiceDeps, finalInfraDeps }
}
